import React, { useState } from 'react';
import styled from 'styled-components';
import { FaStar, FaTimes } from 'react-icons/fa';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
  background: #f52b2b;
  color: white;
  padding: 24px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
`;

const Content = styled.div`
  padding: 24px;
`;

const SessionInfo = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const SessionDetails = styled.div`
  flex: 1;
`;

const SessionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const SessionSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const RatingSection = styled.div`
  margin-bottom: 24px;
`;

const RatingLabel = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0 0 12px 0;
`;

const StarsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 32px;
  color: ${(props) => (props.filled ? '#fbbf24' : '#d1d5db')};
  transition: transform 0.2s, color 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const FeedbackSection = styled.div`
  margin-bottom: 24px;
`;

const FeedbackLabel = styled.label`
  display: block;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #f52b2b;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
`;

const SkipButton = styled(Button)`
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const SubmitButton = styled(Button)`
  background: #f52b2b;
  border: none;
  color: white;

  &:hover {
    background: #dc2626;
  }

  &:disabled {
    background: #fca5a5;
    cursor: not-allowed;
  }
`;

const RateSessionModal = ({ onClose, onSubmit, booking }) => {
  const [rating, setRating] = useState(3);
  const [feedback, setFeedback] = useState('');
  const [hoverRating, setHoverRating] = useState(0);

  const handleRatingClick = (value) => {
    setRating(value);
  };

  const handleFeedbackChange = (e) => {
    if (e.target.value.length <= 500) {
      setFeedback(e.target.value);
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({ rating, feedback });
    }
    onClose();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
          <Title>Rate Your Session</Title>
          <Subtitle>Help others by sharing your experience</Subtitle>
        </Header>

        <Content>
          <SessionInfo>
            <ProfileImage 
              src={booking?.creator_profile_pic?.trim() || "https://via.placeholder.com/48"}
              alt={booking?.creator_name || "Creator"}
            />
            <SessionDetails>
              <SessionTitle>{booking?.session_name || "Session"}</SessionTitle>
              <SessionSubtitle>with {booking?.creator_name || "Creator"}</SessionSubtitle>
            </SessionDetails>
          </SessionInfo>

          <RatingSection>
            <RatingLabel>How would you rate this session?</RatingLabel>
            <StarsContainer>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarButton
                  key={star}
                  filled={star <= (hoverRating || rating)}
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  <FaStar />
                </StarButton>
              ))}
            </StarsContainer>
          </RatingSection>

          <FeedbackSection>
            <FeedbackLabel htmlFor="feedback">
              Share your experience (optional)
            </FeedbackLabel>
            <FeedbackTextarea
              id="feedback"
              value={feedback}
              onChange={handleFeedbackChange}
              placeholder="Tell others about your session experience..."
            />
            <CharacterCount>
              {feedback.length}/500 characters
            </CharacterCount>
          </FeedbackSection>

          <ButtonGroup>
            <SkipButton onClick={onClose}>Skip for Now</SkipButton>
            <SubmitButton onClick={handleSubmit}>
              Submit Review
            </SubmitButton>
          </ButtonGroup>
        </Content>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default RateSessionModal;